import uuidv4 from 'uuid/v4';
import { audioPlayer } from './audio';
import {
  getDefaultRoomId,
  sendWsMessage,
  sendConnectionRequestMessage,
  sendUserQueryMessage,
} from './ws-v1';
import { getAvaBackendEndpoint, clearSearchValue } from './http';
import { log } from './log';
import electron from './electron';

const isTest = process.env.NODE_ENV === 'test';
let socketPromise;
let socketId;
let socket;

const invalidate = socketId_ => {
  if (socketId !== socketId_) {
    return;
  }
  if (socket) {
    socket.close();
  }
  socket = undefined;
  socketId = undefined;
  socketPromise = undefined;
};

const handleWs = (ws, id) => {
  invalidate(socketId);
  socket = ws;
  socketId = id;
  socketPromise = undefined;
};

const createSocket = socketUrl =>
  new Promise((resolve, reject) => {
    const sId = uuidv4();
    const socketOpenTime = Date.now();
    if (isTest) return;
    // eslint-disable-next-line no-console
    console.log('ws-create', sId, socketUrl);

    const ws = new WebSocket(socketUrl);
    const logBackendErrors = ({ data }) => {
      const parsedData = JSON.parse(data);
      if (parsedData && parsedData.type === 'error') {
        // eslint-disable-next-line no-console
        console.error('ws-backend-error', sId, parsedData);
      }
    };
    ws.onopen = () => {
      // eslint-disable-next-line no-console
      console.log('ws-opened', sId, Date.now());
      log('websocketStatus', 'info', {
        event: 'opened',
        url: socketUrl,
        success: true,
      });
      ws.addEventListener('message', logBackendErrors);
      handleWs(ws, sId);
      resolve(ws);
    };
    ws.onerror = error => {
      // eslint-disable-next-line no-console
      console.log('ws-error', sId, JSON.stringify(error, null, 4), error);
      log('websocketStatus', 'error', {
        event: 'error',
        url: socketUrl,
        success: true,
        error,
      });
      invalidate(sId);
      clearSearchValue(window, 'token');
      reject(error);
    };
    ws.onclose = res => {
      // eslint-disable-next-line no-console
      console.log('ws-close', sId, Date.now(), {
        code: res.code,
        reason: res.reason,
        durationS: (Date.now() - socketOpenTime) / 1000,
      });
      log('websocketStatus', 'info', {
        event: 'closed',
        url: socketUrl,
        success: true,
        reason: res.reason,
        code: res.code,
      });
      clearSearchValue(window, 'token');

      ws.removeEventListener('message', logBackendErrors);
      invalidate(sId);
    };
  });

const getDevice = () => {
  const { userAgent } = navigator;
  if (window.isElectron) {
    return 'Ava Desktop';
  }
  const firstInfo = userAgent.match(new RegExp('\\(([^)]+)'))[1];
  let device = '';
  if (firstInfo.toLowerCase().includes('iphone')) {
    device = 'iPhone';
  } else {
    const secondInfo = firstInfo.split('; ');
    if (secondInfo.length) {
      device = secondInfo[secondInfo.length - 1];
    }
  }
  return `${device} -- ${userAgent}`;
};

const getSocket = async () => {
  if (isTest) {
    await new Promise(() => {});
  }
  if (!socket && socketPromise) {
    await socketPromise;
  }
  if (socket) {
    return socket;
  }
  throw new Error('no socket found.');
};

export const createNewSocket = socketUrl => {
  socketPromise = createSocket(socketUrl);
  return socketPromise;
};

export const getSocketIfExist = () => socket;
export const getSocketId = () => socketId;

export default getSocket;

export const createScribeDashboardSocket = url => {
  return createNewSocket(url);
};

export const createUserSearchSocket = async () => {
  const endpoint = getAvaBackendEndpoint();
  let wsEndpoint;
  if (endpoint.includes('https:')) {
    // eslint-disable-next-line
    wsEndpoint = 'wss' + endpoint.split('https')[1];
  } else {
    // eslint-disable-next-line
    wsEndpoint = 'ws' + endpoint.split('http')[1];
  }
  await createNewSocket(`${wsEndpoint}/ws/users-search`);
  const ws = socket;
  // We do not want the WebSocket to be idle, so we periodically query
  // it for a dummy user.
  const interval = setInterval(() => {
    if (
      ws.readyState === WebSocket.CLOSED ||
      ws.readyState === WebSocket.CLOSING
    ) {
      clearInterval(interval);
      return;
    }
    sendUserQueryMessage(ws, '&alcyon');
  }, 1000 * 30);
};

let currentAudioRoomId;

export const createV1Socket = async (url, user, options) => {
  const { roomId, request } = options;
  const scribe = options.scribe && user.features.scribe;
  await createNewSocket(url);
  let downAudio = null;
  const defaultRoomId = getDefaultRoomId(user.uid || user.id);
  let lastRoomId = roomId || defaultRoomId;
  window.onunload = () => {
    if (socket) {
      socket.send(
        JSON.stringify({
          type: 'connection-params-update',
          roomId: getDefaultRoomId(user.uid || user.id),
        })
      );
    }
  };
  socket.addEventListener('message', message =>
    downAudio ? audioPlayer.handleAudioMessage(message) : undefined
  );
  socket.addEventListener('message', message => {
    const data = JSON.parse(message.data);
    if (data.type === 'room-status' || data.type === 'room-status-update') {
      // eslint-disable-next-line no-console
      console.log(data.type, data);

      if (data.type === 'room-status' && data.id && data.id !== lastRoomId) {
        if (scribe) {
          sendWsMessage(socket, {
            type: 'connection-params-update',
            roomId: data.id,
            downAudio: {
              speakerId: data.audioStreams[0].avaId,
            },
          });
        }
      }
      lastRoomId = data.id || lastRoomId;
      if (data.type === 'room-status' && scribe) {
        downAudio = true;
        if (currentAudioRoomId !== roomId) {
          audioPlayer.reset({
            sampleRateHertz: data.audioStreams[0].sampleRateHertz,
            chunkLengthMs: data.downAudio.chunkLengthMs,
            delay: 2000,
            shift: 3,
          });
          currentAudioRoomId = roomId;
        }
      }
    }
  });

  let version = 'web';
  let os = window.navigator.userAgent;
  if (window.isElectron) {
    const {remote: {app, process: {platform}}} = electron;
    version = app.getVersion();
    os = `Electron ${platform}`
  }

  socket.send(
    JSON.stringify({
      type: 'connection-params',
      speakerId: user.parse.avaId,
      roomId: roomId || getDefaultRoomId(user.uid || user.id),
      username: user.parse.userName,
      client: {
        type: 'web',
        appVersion: version,
        os,
        device: getDevice(),
      },
      downAudio: scribe
        ? {
            host: true,
          }
        : undefined,
      conversationMode: 'public',
    })
  );
  if (request && request.avaName) {
    sendConnectionRequestMessage(socket, { avaName: request.avaName });
  }
};

export const createWebappIntegrationWebsocket = async url =>
  createNewSocket(url);
